import {UseFormReturn} from 'react-hook-form';
import {z} from 'zod';

import {ImageQuerySchemaVersion} from './imageEditor';
import {
  createDefaultImageQueryFormData,
  ImageQueryFormData,
  ResizeAspect,
} from './imageQuery';

import type {
  ImageQueryMetadataResponseDto,
  ImageQueryResponseDto,
} from '../dto';

/** Maximale Länge des Bearbeitungs-Namens. */
export const IMAGE_QUERY_NAME_MAX_LENGTH = 256;

/**
 * Flache, an die UI-Felder angelehnte Repräsentation einer Bildbearbeitung.
 * Höhe und Breite werden als String geführt (leerer String = nicht gesetzt),
 * da sie über Textfelder erfasst werden.
 */
export interface ImageQueryFormValues {
  /** Name der Bearbeitung */
  name: string;
  /** Zielhöhe in Pixeln als Roh-Eingabe ('' = nicht gesetzt) */
  height: string;
  /** Zielbreite in Pixeln als Roh-Eingabe ('' = nicht gesetzt) */
  width: string;
  /** Skalierungsverhalten ('' = nicht gesetzt) */
  aspect: ResizeAspect | '';
}

/** Prüft, ob eine Dimensions-Eingabe eine gültige ganze Zahl (≥ 1) ist. */
export const isValidDimension = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return false;
  }
  if (!/^\d+$/.test(trimmed)) {
    return false;
  }
  const numericValue = Number.parseInt(trimmed, 10);
  return numericValue >= 1 && numericValue <= 9999;
};

/** Validierungsschema für ein einzelnes Dimensions-Feld (leer erlaubt). */
const dimensionSchema = z
  .string()
  .refine(value => value.trim() === '' || isValidDimension(value), {
    message: 'Bitte eine gültige Zahl eingeben.',
  });

/** Validierungsschema für das Skalierungsverhalten (leer erlaubt). */
const aspectSchema = z.union([z.enum(ResizeAspect), z.literal('')]);

/**
 * Querfeld-Validierung der Resize-Gruppe: Dimension und Verhalten bedingen
 * sich gegenseitig. Die Meldungen werden am Feld `aspect` ausgegeben, damit
 * sie unterhalb der Verhalten-Auswahl erscheinen.
 */
const refineResizeGroup = (
  values: {height: string; width: string; aspect: ResizeAspect | ''},
  ctx: z.RefinementCtx,
): void => {
  const hasDimension =
    isValidDimension(values.height) || isValidDimension(values.width);
  const hasAspect = values.aspect !== '';

  if (hasDimension && !hasAspect) {
    ctx.addIssue({
      code: 'custom',
      path: ['aspect'],
      message: 'Skalierungsverhalten muss gewählt werden.',
    });
  }
  if (hasAspect && !hasDimension) {
    ctx.addIssue({
      code: 'custom',
      path: ['height'],
      message:
        'Mindestens Höhe oder Breite muss angegeben sein, wenn ein Verhalten gewählt ist.',
    });
    ctx.addIssue({
      code: 'custom',
      path: ['width'],
      message:
        'Mindestens Höhe oder Breite muss angegeben sein, wenn ein Verhalten gewählt ist.',
    });
  }
};

/**
 * Schema für die reinen Formdaten (Resize + Format) – ohne Namen.
 * Erfolgreiche Validierung bedeutet: Vorschau ist möglich.
 */
export const imageQueryFormDataSchema = z
  .object({
    height: dimensionSchema,
    width: dimensionSchema,
    aspect: aspectSchema,
  })
  .superRefine(refineResizeGroup);

/** Validierungsschema für den Namen der Bearbeitung. */
const nameSchema = z
  .string()
  .min(1, 'Der Name muss gesetzt sein.')
  .max(
    IMAGE_QUERY_NAME_MAX_LENGTH,
    `Der Name darf maximal ${IMAGE_QUERY_NAME_MAX_LENGTH} Zeichen lang sein.`,
  );

/**
 * Schema für die gesamte Entität (Formdaten + Name).
 * Erfolgreiche Validierung bedeutet: die Bearbeitung darf gespeichert werden.
 */
export const imageQueryEntitySchema = z
  .object({
    name: nameSchema,
    height: dimensionSchema,
    width: dimensionSchema,
    aspect: aspectSchema,
  })
  .superRefine(refineResizeGroup);

/** Wandelt die flachen Formwerte in versionierte Formdaten um. */
export const formValuesToFormData = (
  values: ImageQueryFormValues,
): ImageQueryFormData => {
  const height = isValidDimension(values.height)
    ? Number.parseInt(values.height.trim(), 10)
    : null;
  const width = isValidDimension(values.width)
    ? Number.parseInt(values.width.trim(), 10)
    : null;
  const hasResize = (height !== null || width !== null) && values.aspect !== '';

  return {
    schemaVersion: ImageQuerySchemaVersion.V1,
    resize: hasResize
      ? {height, width, aspect: values.aspect as ResizeAspect}
      : null,
  };
};

/** Wandelt versionierte Formdaten und einen Namen in flache Formwerte um. */
export const formDataToFormValues = (
  name: string,
  formData: ImageQueryFormData,
): ImageQueryFormValues => {
  const resize = formData.resize;
  return {
    name,
    height: resize && resize.height !== null ? String(resize.height) : '',
    width: resize && resize.width !== null ? String(resize.width) : '',
    aspect: resize?.aspect ?? '',
  };
};

/** Erzeugt die Standard-Formwerte einer neuen, leeren Bearbeitung. */
export const createDefaultImageQueryFormValues = (): ImageQueryFormValues =>
  formDataToFormValues('', createDefaultImageQueryFormData());

/**
 * Kontextwert für die Bildbearbeitungs-Form. Das Formular (react-hook-form) ist
 * die einzige Quelle der Wahrheit für den Draft; Gültigkeit und Dirty-Zustand
 * werden direkt daraus abgeleitet.
 */
export interface ImageQueryFormContextValue {
  /** Metadaten der gespeicherten Bildbearbeitungen des Users (nur ID und Name) */
  imageQueries: ImageQueryMetadataResponseDto[];
  /** Lädt die Bildbearbeitungen neu; optional eine danach zu aktivierende ID */
  refreshImageQueries: (nextActiveQueryId?: number) => void;
  /** Die momentan aktive (geladene) Bildbearbeitung */
  activeImageQuery: ImageQueryResponseDto;
  /** Aktiviert eine Bildbearbeitung und setzt das Formular zurück */
  selectImageQuery: (id: number) => void;
  /** Die react-hook-form Instanz für die Formularfelder */
  form: UseFormReturn<ImageQueryFormValues>;
  /** Aus den aktuellen (gültigen) Formwerten abgeleiteter Draft */
  draftImageQuery: ImageQueryResponseDto;
  /** Sind die Formdaten (Resize + Format) gültig? Steuert die Vorschau. */
  formDataValid: boolean;
  /** Ist die gesamte Entität (Formdaten + Name) gültig? Steuert das Speichern. */
  entityValid: boolean;
  /** Weicht das Formular vom aktiven Stand ab? */
  isDirty: boolean;
  /** Darf gespeichert werden? (entityValid && isDirty) */
  saveEnabled: boolean;
  /** Speichert den Draft (Create oder Update) */
  save: (activateImageQuery: boolean, onSuccessCallback?: () => void) => void;
}
